
import { EmbedType, EmbedCodeOptions } from "@/components/marketing/dialog/types";
import { Form, FormField, FormStyling } from "@/types";

export const generateEmbedCode = (form: Form, options?: EmbedCodeOptions): string => {
  // Use absolute URL for the embed script
  const baseUrl = window.location.origin;
  const formId = form.id;
  
  // Default to iframe embed if no options provided
  const embedType = options?.type || EmbedType.IFRAME;
  
  if (embedType === EmbedType.FLOATING_BUTTON) {
    const companyName = options?.companyName || form.content_settings?.form_content?.company_name || 'this company';
    const buttonColor = options?.buttonColor || form.styling?.buttonColor || '#6366f1';
    const buttonTextColor = options?.buttonTextColor || form.styling?.buttonTextColor || '#ffffff';
    // Prefer styling.logoUrl, but fall back to form_content.logo_url if available
    const logoUrl = options?.logoUrl || form.styling?.logoUrl || form.content_settings?.form_content?.logo_url || '';
    
    return `
<!-- Investor Form Floating Button -->
<script src="${baseUrl}/embed/floating-button.js" 
  id="investor-form-button" 
  data-form-id="${formId}" 
  data-company-name="${companyName}" 
  data-button-color="${buttonColor}" 
  data-button-text-color="${buttonTextColor}"
  ${logoUrl ? `data-logo-url="${logoUrl}"` : ''}
  data-csp-compliant="true" 
  defer>
</script>
<!-- End Investor Form Widget -->
    `;
  }
  
  // Update iframe embed code with additional parameters for authentication support
  return `
<!-- Investor Form Widget -->
<div id="investor-form-${formId}" style="width:100%; height:auto; min-height:500px;"></div>
<script src="${baseUrl}/embed/form.js" id="investor-form-script" data-form-id="${formId}" data-csp-compliant="true" defer></script>
<!-- End Investor Form Widget -->

<script type="module">
  // Set initial height
  const container = document.getElementById('investor-form-${formId}');
  if (container) container.style.height = '500px';

  let height = 0
  let resizeTimer;

  function sendEmbedHeight() {
    window.parent.postMessage({
      embedHeight: height
    }, "*")
  }

  // Create resize observer to track content height changes
  const observer = new ResizeObserver((entries) => {
    if (entries.length !== 1) return
    const entry = entries[0]
    if (entry.target !== document.body) return

    height = entry.contentRect.height
    
    // Set container height with a small buffer to avoid scrollbars
    if (container) container.style.height = (height + 50) + 'px';
    
    // Debounce frequent updates
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      sendEmbedHeight()
    }, 100);
  })

  observer.observe(document.body)

  window.addEventListener("message", (event) => {
    if (event.source !== window.parent) return
    if (event.data !== "getEmbedHeight") return
    sendEmbedHeight()
  })
</script>
  `;
};

export const validateFormField = (field: FormField, value: string): { valid: boolean; message?: string } => {
  // Check required
  if (field.required && (!value || value.trim() === '')) {
    return { valid: false, message: `${field.label} is required` };
  }

  // Validation based on field type
  switch (field.type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        return { valid: false, message: 'Please enter a valid email address' };
      }
      break;
    case 'phone':
      const phoneRegex = /^\+?[0-9\s\-()]+$/;
      if (value && !phoneRegex.test(value)) {
        return { valid: false, message: 'Please enter a valid phone number' };
      }
      break;
    case 'number':
      if (value && isNaN(Number(value))) {
        return { valid: false, message: 'Please enter a valid number' };
      }
      break;
  }

  // Custom validation
  if (field.validation && field.validation.pattern && value) {
    try {
      const regex = new RegExp(field.validation.pattern);
      if (!regex.test(value)) {
        return { valid: false, message: `${field.label} is not in the right format` };
      }
    } catch (e) {
      console.error('Invalid regex pattern:', field.validation.pattern);
    }
  }

  return { valid: true };
};

export const getDefaultFormTemplate = (): Form => {
  return {
    id: "",
    name: "New Form",
    description: "Real estate investor form",
    fields: [
      {
        id: crypto.randomUUID(),
        type: "text",
        label: "Full Name",
        placeholder: "Enter your full name",
        required: true,
        order: 0
      },
      {
        id: crypto.randomUUID(),
        type: "email",
        label: "Email Address",
        placeholder: "Enter your email address",
        required: true,
        order: 1
      },
      {
        id: crypto.randomUUID(),
        type: "phone",
        label: "Phone Number",
        placeholder: "Enter your phone number",
        required: true,
        order: 2
      }
    ],
    styling: {
      primaryColor: "#6366f1",
      fontFamily: "Inter, sans-serif",
      borderRadius: "0.375rem",
      backgroundColor: "#ffffff",
      textColor: "#1f2937",
      buttonColor: "#6366f1",
      buttonTextColor: "#ffffff",
    },
    createdAt: new Date(),
    updatedAt: new Date(),
    userId: "", // This will be set when creating a new form
    isPublished: false,
    views: 0
  };
};

// Export the default form to fix the missing import error
export const defaultForm = getDefaultFormTemplate();
